import { destroy, detach, getRoot, Instance, types } from 'mobx-state-tree';
import {
  ButtonModel,
  DropdownModel,
  DropdownModelType,
  DropdownOptionModel,
  PageModel,
  Partner,
  RootType,
} from '../../../internal';
import i18n from '../../../i18n';

const DROPDOWN_PREFIX = 'BrandsDropdown-';
const DEFAULT_OPTION_PREFIX = 'defaultOption';
const SAVE_BUTTON_ID = 'SavePartnersButton';

export const RecommendedBrandsModel = types
  .compose(
    PageModel,
    types.model({
      activePartners: types.frozen<any[]>([]),
      alreadySelectedPartners: types.map(types.string),
      dropdowns: types.array(DropdownModel),
      numberOfDropdowns: 0,
      totalNumberOfFields: 80,
    })
  )
  .views((self) => ({
    get root(): RootType {
      return getRoot(self);
    },
    get availablePartnerOptions() {
      return self.activePartners.filter(
        (partner) => !self.alreadySelectedPartners.get(partner.name)
      );
    },
    getDefaultPartnerOption(dropdownId: string) {
      return DropdownOptionModel.create({
        id: DEFAULT_OPTION_PREFIX + dropdownId,
        value: 'Izaberi',
      });
    },
  }))
  .actions((self) => ({
    beforeExit() {
      this.clearData();
    },
    async beforePageEnter() {
      try {
        await this.loadActivePartners();
        await this.loadFavoritePartners();
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    },
    async loadActivePartners() {
      const response = await self.root.api.partner.getAllActivePartners();
      this.setActivePartners(response.data);
    },
    async loadFavoritePartners() {
      const response = await self.root.api.recommendedBrands.getList();
      response.forEach(
        (favoritePartner: { partnerId: string | undefined }, index: number) => {
          const dropdownId = `${DROPDOWN_PREFIX}${index + 1}`;
          if (
            self.dropdowns.findIndex((dropdown) => dropdown.id === dropdownId) <
            0
          ) {
            this.createAndFillDropdownWithOptions(
              dropdownId,
              favoritePartner.partnerId
            );
          }
        }
      );
      this.setNumberOfDropdowns(response.length);
    },
    setNumberOfDropdowns(number: number) {
      self.numberOfDropdowns = number;
    },
    setActivePartners(partners: Partner[]) {
      self.activePartners = partners;
    },
    clearDropdowns() {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      self.dropdowns = [];
    },
    clearData() {
      self.componentsArray.forEach((component) => {
        detach(component);
        destroy(component);
      });
      self.components.clear();
    },
    createAndFillDropdownWithOptions(
      dropdownId: string,
      alreadySelectedValue?: string
    ) {
      const partnerOptions = self.availablePartnerOptions.map((partner) =>
        DropdownOptionModel.create({
          id: partner.id + dropdownId,
          value: partner.name,
        })
      );
      const dropdownModel = DropdownModel.create({
        id: dropdownId,
        label: '',
        options: {},
        onChangeCallback: (option: { id: string; value: string }) => {
          this.setAlreadySelectedPartners(option.value);
        },
      });
      const defaultOption = self.getDefaultPartnerOption(dropdownId);
      partnerOptions.unshift(defaultOption);
      dropdownModel.setOptions(partnerOptions);
      if (alreadySelectedValue) {
        const indexOfOption = partnerOptions.findIndex(
          (option) => option.id === alreadySelectedValue + dropdownId
        );
        if (indexOfOption !== -1) {
          dropdownModel.selectOption(indexOfOption);
        }
      }
      self.dropdowns.push(dropdownModel);
    },
    setAlreadySelectedPartners(value: string) {
      self.alreadySelectedPartners = self.alreadySelectedPartners.set(
        value,
        value
      );
    },
    setDropdownValue(id: string, value: string) {
      const dropdown = self.dropdowns.find((dropdown) => dropdown.id === id);
      if (dropdown) {
        dropdown.setValue(value);
      }
    },
    swapDropdowns(currentId: string, targetId: string) {
      const currentIndex = self.dropdowns.findIndex(
        (dropdown) => dropdown.id === currentId
      );
      const targetIndex = self.dropdowns.findIndex(
        (dropdown) => dropdown.id === targetId
      );

      if (
        currentIndex !== -1 &&
        targetIndex !== -1 &&
        currentIndex !== targetIndex
      ) {
        const updatedDropdowns = [...self.dropdowns];
        const [movedItem] = updatedDropdowns.splice(currentIndex, 1);
        updatedDropdowns.splice(targetIndex, 0, movedItem);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        self.dropdowns = updatedDropdowns;
      }
    },
    addDropdown() {
      self.numberOfDropdowns = self.numberOfDropdowns + 1;
      this.createAndFillDropdownWithOptions(
        `${DROPDOWN_PREFIX}${self.numberOfDropdowns}`
      );
    },
    async saveCurrentPartners() {
      const values = self.dropdowns
        .filter((dropdown) => !dropdown.value.includes('default'))
        .map((dropdown) => dropdown.value.split('Brand')[0]);
      return await self.root.api.recommendedBrands.savePartners(values);
    },
    getSaveButton() {
      return (
        self.root.currentPage.components.get(SAVE_BUTTON_ID) ||
        self.root.currentPage.addComponent(
          ButtonModel.create({
            id: SAVE_BUTTON_ID,
            label: i18n.t('basic:save_changes'),
            onClickCallback: () => {
              this.saveCurrentPartners();
            },
            disabled: false,
          })
        )
      );
    },
    detachModel(model: DropdownModelType) {
      detach(model);
    },
  }))
  .named('RecommendedBrandsModel');

export type RecommendedBrandsPageType = Instance<typeof RecommendedBrandsModel>;
